/**
 * Placement test: an adaptive item picker over a levelled bank (HSK 1–6, CEFR A1–C1).
 *
 * The learner's ability θ lives on the bank's level scale (1 = lowest level). After each
 * answer the posterior over a θ grid is updated with a two-parameter logistic model plus
 * guessing (a «Не знаю» answer carries no guessing); the next item is the unused one whose
 * level is closest to the posterior mean, rotating skills. The test stops after 12 items
 * once the estimate is tight, and always after 15.
 */

export const PLACEMENT_SKILLS = ['vocab', 'grammar', 'reading', 'listening'] as const;
export type PlacementSkill = (typeof PLACEMENT_SKILLS)[number];

export interface PlacementItem {
  id: string;
  /** 1-based level on the bank's scale. */
  level: number;
  skill: PlacementSkill;
  /** Instruction or question in Russian. */
  prompt: string;
  /** Stimulus shown in the target language (word, gapped sentence, short passage). */
  text?: string;
  /** Spoken stimulus for listening items (not shown as text). */
  audio?: string;
  options: string[];
  answer: string;
  explanation_ru: string;
}

export interface PlacementBank {
  lang: string;
  version: number;
  /** Level labels, index 0 = level 1. */
  scale: string[];
  items: PlacementItem[];
}

export type PlacementOutcome = 'correct' | 'wrong' | 'dontknow';

export interface PlacementResponse {
  itemId: string;
  level: number;
  skill: PlacementSkill;
  options: number;
  outcome: PlacementOutcome;
}

export const PLACEMENT_MIN_ITEMS = 12;
export const PLACEMENT_MAX_ITEMS = 15;
const STOP_SD = 0.55;
const DISCRIMINATION = 1.7;
const GRID_STEP = 0.05;

const logistic = (x: number) => 1 / (1 + Math.exp(-x));

/** Probability of a correct answer at ability θ for an item of level `b` with `options` choices. */
export function pCorrect(theta: number, level: number, options: number): number {
  const guess = options > 0 ? 1 / options : 0;
  return guess + (1 - guess) * logistic(DISCRIMINATION * (theta - level));
}

function likelihood(theta: number, r: PlacementResponse): number {
  if (r.outcome === 'correct') return pCorrect(theta, r.level, r.options);
  if (r.outcome === 'dontknow') return 1 - logistic(DISCRIMINATION * (theta - r.level));
  return 1 - pCorrect(theta, r.level, r.options);
}

export interface Estimate {
  mean: number;
  sd: number;
}

/** Posterior mean and SD of θ on [0.5, levels + 0.5], with a broad prior centred mid-scale. */
export function estimateAbility(responses: readonly PlacementResponse[], levels: number): Estimate {
  const lo = 0.5;
  const hi = levels + 0.5;
  const mid = (levels + 1) / 2;
  let total = 0;
  let sum = 0;
  let sumSq = 0;
  for (let theta = lo; theta <= hi + 1e-9; theta += GRID_STEP) {
    let w = Math.exp(-((theta - mid) ** 2) / (2 * 2.5 ** 2));
    for (const r of responses) w *= likelihood(theta, r);
    total += w;
    sum += w * theta;
    sumSq += w * theta * theta;
  }
  if (total === 0) return { mean: mid, sd: levels };
  const mean = sum / total;
  return { mean, sd: Math.sqrt(Math.max(0, sumSq / total - mean * mean)) };
}

export function shouldStop(responses: readonly PlacementResponse[], levels: number): boolean {
  if (responses.length >= PLACEMENT_MAX_ITEMS) return true;
  if (responses.length < PLACEMENT_MIN_ITEMS) return false;
  return estimateAbility(responses, levels).sd < STOP_SD;
}

function hash(text: string): number {
  let h = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 4294967296;
}

/**
 * The next item: unused, level closest to the current estimate, skills rotated so the
 * test samples words, grammar, reading and listening. `seed` varies the pick between
 * attempts without making a given attempt non-deterministic. Listening items are skipped
 * when the device cannot speak.
 */
export function nextItem(
  bank: PlacementBank,
  responses: readonly PlacementResponse[],
  seed: string,
  opts: { listening?: boolean } = {},
): PlacementItem | null {
  const used = new Set(responses.map((r) => r.itemId));
  const levels = bank.scale.length;
  const target = estimateAbility(responses, levels).mean;
  const skillCount = new Map<PlacementSkill, number>();
  for (const r of responses) skillCount.set(r.skill, (skillCount.get(r.skill) ?? 0) + 1);
  const lastSkill = responses.at(-1)?.skill;
  const allowListening = opts.listening ?? true;

  let best: PlacementItem | null = null;
  let bestScore = Infinity;
  for (const item of bank.items) {
    if (used.has(item.id)) continue;
    if (item.skill === 'listening' && !allowListening) continue;
    const score =
      Math.abs(item.level - target) +
      0.35 * (skillCount.get(item.skill) ?? 0) +
      (item.skill === lastSkill ? 0.4 : 0) +
      0.3 * hash(`${seed}|${item.id}`);
    if (score < bestScore) {
      bestScore = score;
      best = item;
    }
  }
  return best;
}

export type SkillVerdict = 'strong' | 'ok' | 'weak';

export interface PlacementResult {
  lang: string;
  /** Rounded level, 1..levels. */
  level: number;
  label: string;
  mean: number;
  sd: number;
  skills: Partial<Record<PlacementSkill, SkillVerdict>>;
  answered: number;
  correct: number;
}

/** Final estimate plus per-skill strengths (observed vs expected at the estimate). */
export function placementResult(bank: PlacementBank, responses: readonly PlacementResponse[]): PlacementResult {
  const levels = bank.scale.length;
  const { mean, sd } = estimateAbility(responses, levels);
  const level = Math.min(levels, Math.max(1, Math.round(mean)));
  const skills: Partial<Record<PlacementSkill, SkillVerdict>> = {};
  for (const skill of PLACEMENT_SKILLS) {
    const rs = responses.filter((r) => r.skill === skill);
    if (rs.length < 2) continue;
    const residual =
      rs.reduce((s, r) => s + (r.outcome === 'correct' ? 1 : 0) - pCorrect(mean, r.level, r.options), 0) / rs.length;
    skills[skill] = residual > 0.15 ? 'strong' : residual < -0.15 ? 'weak' : 'ok';
  }
  return {
    lang: bank.lang,
    level,
    label: bank.scale[level - 1] ?? String(level),
    mean: Math.round(mean * 100) / 100,
    sd: Math.round(sd * 100) / 100,
    skills,
    answered: responses.length,
    correct: responses.filter((r) => r.outcome === 'correct').length,
  };
}

/** Schema and coverage checks for a bank; an empty list means the bank is usable. */
export function validateBank(bank: PlacementBank, minPerLevel = 8): string[] {
  const issues: string[] = [];
  const ids = new Set<string>();
  const levels = bank.scale.length;
  if (levels < 2) issues.push('scale: need at least two levels');
  for (const item of bank.items) {
    const where = item.id || '(no id)';
    if (!item.id) issues.push(`${where}: missing id`);
    if (ids.has(item.id)) issues.push(`${where}: duplicate id`);
    ids.add(item.id);
    if (!Number.isInteger(item.level) || item.level < 1 || item.level > levels) issues.push(`${where}: bad level`);
    if (!(PLACEMENT_SKILLS as readonly string[]).includes(item.skill)) issues.push(`${where}: bad skill`);
    if (!item.prompt?.trim()) issues.push(`${where}: missing prompt`);
    if (!item.explanation_ru?.trim()) issues.push(`${where}: missing explanation`);
    if (item.skill === 'listening' ? !item.audio?.trim() : !item.text?.trim()) issues.push(`${where}: missing stimulus`);
    if (!Array.isArray(item.options) || item.options.length < 3) issues.push(`${where}: need 3+ options`);
    else {
      if (new Set(item.options).size !== item.options.length) issues.push(`${where}: duplicate options`);
      if (item.options.filter((o) => o === item.answer).length !== 1) issues.push(`${where}: answer must match exactly one option`);
    }
  }
  for (let level = 1; level <= levels; level += 1) {
    const n = bank.items.filter((i) => i.level === level).length;
    if (n < minPerLevel) issues.push(`level ${level}: only ${n} items`);
  }
  return issues;
}

// ── Where to start ─────────────────────────────────────────────────────────────────────

/** Level of each known book on its language's placement scale (HSK 1–6, A1=1…C1=5). */
const BOOK_LEVELS: Readonly<Record<string, number>> = {
  'zh-hsk4': 4,
  'zh-hsk5-a': 5,
  'zh-hsk5-b': 5.5,
  'zh-hsk6': 6,
  'en-evolve-1': 1,
  'en-evolve-2': 2,
  'en-evolve-3': 3,
  'en-evolve-4': 3.5,
  'en-evolve-5': 4,
  'en-evolve-6': 4.5,
};

export function bookLevel(bookId: string): number | null {
  if (bookId in BOOK_LEVELS) return BOOK_LEVELS[bookId]!;
  const prefix = Object.keys(BOOK_LEVELS)
    .filter((k) => bookId.startsWith(`${k}-`))
    .sort((a, b) => b.length - a.length)[0];
  return prefix ? BOOK_LEVELS[prefix]! : null;
}

export interface StartBook {
  id: string;
  sort: number;
}
export interface StartLesson {
  id: string;
  book_id: string;
  number: number;
}

export interface StartRecommendation {
  bookId: string;
  lessonId: string;
  /** Russian note when the course is clearly above or below the learner's level. */
  note: string | null;
}

/**
 * The first lesson of the hardest book at or below the estimate (with a little slack), or
 * of the easiest book when everything is above it.
 */
export function recommendStart(
  mean: number,
  books: readonly StartBook[],
  lessons: readonly StartLesson[],
): StartRecommendation | null {
  const levelled = books
    .map((b) => ({ ...b, level: bookLevel(b.id) }))
    .filter((b): b is StartBook & { level: number } => b.level !== null && lessons.some((l) => l.book_id === b.id))
    .sort((a, b) => a.level - b.level || a.sort - b.sort);
  if (levelled.length === 0) return null;
  const fitting = levelled.filter((b) => b.level <= mean + 0.25);
  const book = fitting.at(-1) ?? levelled[0]!;
  const first = lessons.filter((l) => l.book_id === book.id).sort((a, b) => a.number - b.number)[0]!;
  const easiest = levelled[0]!.level;
  const hardest = levelled.at(-1)!.level;
  const note =
    mean < easiest - 0.75
      ? 'Курс пока сложнее вашего уровня — начните с первого урока и не спешите.'
      : mean > hardest + 1
        ? 'Курс ниже вашего уровня — начните с последней книги, предыдущие уроки открыты.'
        : null;
  return { bookId: book.id, lessonId: first.id, note };
}
